'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Send, BarChart2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface NewsletterFormData {
  full_name: string;
  email: string;
}

export default function NewsletterWidget() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const supabase = createClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewsletterFormData>();

  const onSubmit = async (data: NewsletterFormData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from('email_subscribers').insert({
        full_name: data.full_name.trim(),
        email: data.email.trim().toLowerCase(),
      });
      if (error) {
        if (error.code === '23505') {
          toast.error('This email is already subscribed!');
        } else {
          throw error;
        }
      } else {
        toast.success(`Subscribed! Welcome to DMTV, ${data.full_name}!`);
        reset();
      }
    } catch (err: any) {
      toast.error(err?.message || 'Subscription failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4">
        {/* Header with illustration hint */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="text-base font-semibold text-foreground">
              Stay Ahead of the Market
            </h2>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Get the latest crypto news, analysis, and market updates straight to your inbox.
            </p>
          </div>
          <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <BarChart2 size={22} className="text-primary" />
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-2">
          <div>
            <input
              type="text"
              placeholder="Your full name"
              {...register('full_name', {
                required: 'Name is required',
                minLength: { value: 2, message: 'Name must be at least 2 characters' },
              })}
              className={`w-full bg-input border rounded px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-colors duration-150
                ${errors.full_name ? 'border-crypto-red' : 'border-border'}`}
            />
            {errors.full_name && (
              <p className="text-crypto-red text-[11px] mt-1">{errors.full_name.message}</p>
            )}
          </div>

          <div>
            <input
              type="email"
              placeholder="Enter your email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address',
                },
              })}
              className={`w-full bg-input border rounded px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-colors duration-150
                ${errors.email ? 'border-crypto-red' : 'border-border'}`}
            />
            {errors.email && (
              <p className="text-crypto-red text-[11px] mt-1">{errors.email.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center justify-center gap-2 w-full bg-crypto-green hover:bg-crypto-green/90 disabled:opacity-60 disabled:cursor-not-allowed text-white px-4 py-2.5 rounded text-sm font-semibold transition-all duration-150 active:scale-95"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Subscribing...
              </>
            ) : (
              <>
                <Send size={14} />
                Subscribe
              </>
            )}
          </button>
        </form>

        <p className="text-[10px] text-muted-foreground mt-2 text-center">
          No spam. Unsubscribe anytime.
        </p>
      </div>
    </div>
  );
}